// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { HttpRequest } from '@angular/common/http';

export type ActionId = number;

interface Action {
  type: string;
  id: ActionId;
  pattern: RegExp;
  numInvocations: number;
}

interface Delay extends Action {
  type: 'delay';
  delayMS: number;
}

interface Failure extends Action {
  type: 'failure';
  errorCode: number;
}

const actions: Action[] = [];

/**
 * configures mock to fail next network call whose url matches pattern
 * returns specified error code instead
 */
export function failNextCall(
  pattern: RegExp,
  errorCode: number,
  numFailures = 1
): ActionId {
  const failure: Failure = {
    type: 'failure',
    id: Math.random(),
    pattern,
    errorCode,
    numInvocations: numFailures,
  };
  actions.push(failure);
  return failure.id;
}

/**
 * configures mock to delay next network call whose url matches pattern
 * returns ref to delay object (can be removed using deleteDelay())
 */
export function delayNextCall(
  pattern: RegExp,
  delayMS: number,
  numDelays = 1
): ActionId {
  const delay: Delay = {
    type: 'delay',
    id: Math.random(),
    pattern,
    delayMS,
    numInvocations: numDelays,
  };
  actions.push(delay);
  return delay.id;
}

/**
 * force remove standing delay
 */
export function deleteAction(actionId: ActionId): boolean {
  const match = actions.findIndex(({ id }) => id === actionId);
  if (match) {
    actions.splice(match, 1);
    return true;
  }
  return false;
}

/**
 * delays an actual api call
 */
export async function performActionIfRequestd(
  req: HttpRequest<unknown>
): Promise<void> {
  const matchedActions = actions.filter((d) =>
    req.url.toString().match(d.pattern)
  );
  for (const action of matchedActions) {
    if (!tryPruneAction(action)) {
      action.numInvocations--;
      if (action.type === 'delay') {
        const delay = action as Delay;
        await new Promise((r) => setTimeout(r, delay.delayMS));
      }
      if (action.type === 'failure') {
        const failure = action as Failure;
        throw new Error(`${failure.errorCode}`);
      }
    }
  }
}

function tryPruneAction(action: Action): boolean {
  if (action.numInvocations === 0) {
    actions.splice(
      actions.findIndex((d) => d === action),
      1
    );
    return true;
  }
  return false;
}

const TEST_HELPERS = {
  delayNextCall,
  deleteDelay: deleteAction,
  failNextCall,
};
export type TTestHelpers = typeof TEST_HELPERS;

// use this during testing
(window as unknown as any).TEST_HELPERS = TEST_HELPERS;
